import { applySnapshot, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { IInput } from "../../utils/common-interface";
import { ExistingDebtPlanListModel } from "../existingdebtplan/ExistingDebtPlanListModel";
import { PlanSummaryModel } from "../existingdebtplan/ExistingDebtPlanModel";
import existingDebtPlanAPI from "../existingdebtplan/ExistingDebtPlanService";
import { FinancialReportListModel } from "../financial/FinancialReportListModel";
import { FinancialReportModel } from "../financial/FinancialReportModel";
import financialReportAPI from "../financial/FinancialReportService";
import { NewDebtPlanListModel } from "../newdebtplan/NewDebtPlanListModel";
import { NewDebtPlanSummaryModel } from "../newdebtplan/NewDebtPlanModel";
import newDebtPlanAPI from "../newdebtplan/NewDebtPlanService";
import { ShareHolderModel } from "../org/OrganizeModel";

type DebtPlanChartData = {
  // Data by payment type (ชำระตามกำหนด, ชำระล่วงหน้า, etc.)
  paymentPlanData: Array<{
    name: string;
    เงินกู้ในประเทศ: number;
    เงินกู้ต่างประเทศ: number;
    totalDisplay: number;
    loanSourceData: Array<{
      name: string;
      เงินกู้ในประเทศ: number;
      เงินกู้ต่างประเทศ: number;
    }>;
  }>;

  // Total amount for headers
  totalAmount: number;
};

export const YearPlanDashboardModel = types
  .model("YearPlanDashboardModel", {
    newDebtPlanSummary: types.optional(NewDebtPlanSummaryModel, {}),
    newDebtPlanList: types.optional(NewDebtPlanListModel, {}),
    existingDebtPlanSummary: types.optional(PlanSummaryModel, {}),
    existingDebtPlanPaymentAndRestructureSummary: types.optional(
      PlanSummaryModel,
      {}
    ),
    existingDebtPlanList: types.optional(ExistingDebtPlanListModel, {}),
    financialReport: types.optional(FinancialReportModel, {}),
    financialReportList: types.optional(FinancialReportListModel, {}),
    organization: types.optional(ShareHolderModel, {}), //  ShareHolderModel เพราะต้องใช้กับ OrganizationDropdown
    planStatus: types.optional(types.string, ""),
    planRelease: types.optional(types.string, ""),
    fiscalYear: types.maybe(types.number), // สำหรับไว้ Search
    fiscalYearLabel: types.optional(types.string, "-"), // เพื่อเอาไปแสดงที่ Summarydashboard
    error: types.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false),
    planid: types.maybeNull(types.number),
  })
  .views((self: any) => ({
    getLoanAmountTypeA() {
      // ตรวจสอบว่า newDebtPlanSummary และ overAllByLoanSource มีการโหลดแล้วหรือไม่
      if (
        !self.newDebtPlanSummary ||
        !self.newDebtPlanSummary.overAllByLoanSource
      ) {
        return 0;
      }

      // ค้นหาข้อมูลเงินกู้ที่มี sourceType เป็น "A" ด้วยวิธีที่ยืดหยุ่นมากขึ้น
      const loanTypeA = self.newDebtPlanSummary.overAllByLoanSource.find(
        (loan: any) => {
          if (!loan || typeof loan.sourceType !== "string") return false;
          return loan.sourceType.trim() === "A";
        }
      );

      // ถ้าไม่พบ log ออกมาและ return ค่าที่กำหนด
      if (!loanTypeA) {
        return 2; // ค่าที่คุณต้องการให้ return เมื่อไม่พบข้อมูล
      }

      // ดำเนินการต่อหากพบข้อมูล
      if (
        !self.newDebtPlanSummary.currencyData ||
        !self.newDebtPlanSummary.currencyData.currency
      ) {
        return loanTypeA.loanAmount;
      }

      const currencyObj = self.newDebtPlanSummary.currencyData.currency.find(
        (curr: any) => curr.currencyCode === loanTypeA.currency
      );

      const currencyRate = currencyObj ? currencyObj.currencyRate : 1.0;

      // คำนวณผลลัพธ์
      return (loanTypeA.loanAmount * currencyRate) / 1000000;
    },

    get getDebtRestructureChartData() {
      // Check if data exists
      if (
        !self.existingDebtPlanPaymentAndRestructureSummary!
          .restructurePlanSummary!.paymentPlans
      ) {
        return [];
      }

      const paymentPlans =
        self.existingDebtPlanPaymentAndRestructureSummary.restructurePlanSummary
          .paymentPlans;

      // Create a mapping between debtPaymentPlanType codes and display names
      const planTypeMapping = {
        ROLL: "ปรับโครงสร้างหนี้จากการ Roll Over",
        REF: "ปรับโครงสร้างหนี้จากการ Refinance",
        RORF: "ปรับโครงสร้างหนี้จากการ Roll Over + Refinance",
        CCS: "บริหารความเสี่ยง CCS",
        IRS: "บริหารความเสี่ยง IRS",
        COIR: "บริหารความเสี่ยง CCS + IRS",
        NA: "อื่น ๆ",
      } as const;

      type PlanTypeKey = keyof typeof planTypeMapping;
      const planTypeKeys = Object.keys(planTypeMapping) as PlanTypeKey[];

      // Create a result object with the required structure
      const result = planTypeKeys.map((planType) => {
        // Find the corresponding payment plan
        const plan = paymentPlans.find(
          (p: any) => p.debtPaymentPlanType === planType
        );

        // Default values if plan doesn't exist
        let domesticLoan = 0;
        let foreignLoan = 0;

        if (plan) {
          // Find domestic and foreign loan amounts
          const domesticSource = plan.loanSourcePlans.find(
            (l: any) => l.sourceType === "L"
          );
          const foreignSource = plan.loanSourcePlans.find(
            (l: any) => l.sourceType === "F"
          );

          domesticLoan = domesticSource
            ? domesticSource.thbAmount / 1000000
            : 0; // Convert to millions
          foreignLoan = foreignSource ? foreignSource.thbAmount / 1000000 : 0; // Convert to millions
        }

        return {
          name: planTypeMapping[planType],
          เงินกู้ในประเทศ: domesticLoan,
          เงินกู้ต่างประเทศ: foreignLoan,
          totalDisplay: domesticLoan + foreignLoan,
        };
      });

      return result;
    },

    get getBudgetOverallSummary() {
      // Default return value if no data
      const emptyResult = {
        overAllSummary: [],
        totalAmount: 0,
      };

      // Check if base data exists
      if (!self.existingDebtPlanPaymentAndRestructureSummary) {
        return emptyResult;
      }

      // Get overAllSummary from MST model
      const overAllSummary = self.existingDebtPlanPaymentAndRestructureSummary.overAllSummary;

      // Check for MST ObservableArray - it has length property but isArray = false
      if (!overAllSummary || (typeof overAllSummary.length !== 'number')) {
        return emptyResult;
      }

      if (overAllSummary.length === 0) {
        return emptyResult;
      }

      // Process the data - work with ObservableArray like normal array
      const budgetCategories: Array<{
        title: string;
        value: number;
        principalValue: number;
        interestValue: number;
      }> = [];

      // Use for loop instead of forEach for ObservableArray
      for (let index = 0; index < overAllSummary.length; index++) {
        const item = overAllSummary[index];
        
        if (item && item.title) {
          const principalAmount = (item.principalValue || 0) / 1000000;
          const interestAmount = (item.interestValue || 0) / 1000000;
          const totalAmount = principalAmount + interestAmount;
          
          budgetCategories.push({
            title: item.title,
            value: totalAmount,
            principalValue: principalAmount,
            interestValue: interestAmount,
          });
          
         } else {
          console.log(`❌ Skipped item ${index} - no title:`, item);
        }
      }

      // Calculate total amount
      const totalAmount = budgetCategories.reduce((sum, category) => sum + category.value, 0);
      
      const result = {
        overAllSummary: budgetCategories,
        totalAmount: totalAmount,
      };
      
      return result;
    },

    get getDebtPlanChartData() {
      // Default return value if no data
      const emptyResult = {
        paymentPlanData: [],
        totalAmount: 0,
      };

      // Check if data is available
      if (
        !self.existingDebtPlanPaymentAndRestructureSummary ||
        !self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary ||
        !self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary
          .paymentPlans
      ) {
        return emptyResult;
      }

      const paymentPlans =
        self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary
          .paymentPlans;

      // Total amount (useful for headers)
      const totalAmount =
        self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary
          .paymentPlanSumAmountTHB || 0;

      // Types definitions for payment plans and loan sources
      type PaymentPlan = {
        paymentSource?: string;
        debtPaymentPlanType?: string;
        loanSourcePlans?: LoanSource[];
      };

      type LoanSource = {
        sourceType?: string;
        thbAmount?: number;
        loanAmount?: number;
      };

      // 1. Process data by payment plan type with an empty loanSourceData array
      const planTypeMap = new Map<
        string,
        {
          name: string;
          เงินกู้ในประเทศ: number;
          เงินกู้ต่างประเทศ: number;
          totalDisplay: number;
          loanSourceData: Array<{
            name: string;
            เงินกู้ในประเทศ: number;
            เงินกู้ต่างประเทศ: number;
            totalDisplaySource: number;
          }>;
        }
      >();

      // Define payment plan types in Thai
      const planTypeLabels = {
        SCH: "ชำระตามกำหนด",
        ADV: "ชำระล่วงหน้า",
      };

      // 2. Define source type categories
      const sourceLabels: Record<string, string> = {
        "03": "เงินงบประมาณ",
        "04": "เงินรายได้",
        "07": "เงินจากแหล่งอื่นๆ",
        "L": "เงินกู้",
      };

      // Maps to track source data per plan type
      const sourceDataByPlanType = new Map<
        string,
        Map<string, { เงินกู้ในประเทศ: number; เงินกู้ต่างประเทศ: number }>
      >();

      // Initialize plan types
      Object.values(planTypeLabels).forEach((planName) => {
        planTypeMap.set(planName, {
          name: planName,
          เงินกู้ในประเทศ: 0,
          เงินกู้ต่างประเทศ: 0,
          totalDisplay: 0,
          loanSourceData: [],
        });

        // Initialize source data tracking for this plan type
        sourceDataByPlanType.set(planName, new Map());
        Object.values(sourceLabels).forEach((sourceName) => {
          sourceDataByPlanType.get(planName)!.set(sourceName, {
            เงินกู้ในประเทศ: 0,
            เงินกู้ต่างประเทศ: 0,
          });
        });
      });

      // Process each payment plan
      paymentPlans.forEach((plan: PaymentPlan) => {
        // Get plan type
        const planType = (plan.debtPaymentPlanType ||
          "OTH") as keyof typeof planTypeLabels;
        const planName = planTypeLabels[planType] || "อื่น ๆ";

        // Skip if not a recognized plan type
        if (!planTypeMap.has(planName)) return;

        // Get source type
        const sourceName =
          plan.paymentSource && plan.paymentSource in sourceLabels
            ? sourceLabels[plan.paymentSource]
            : "อื่นๆ";

        // Process loan sources for this plan
        if (plan.loanSourcePlans && plan.loanSourcePlans.length > 0) {
          plan.loanSourcePlans.forEach((loanSource: LoanSource) => {
            const amount = loanSource.thbAmount || loanSource.loanAmount || 0;

            // Update plan type totals (in millions)
            const planEntry = planTypeMap.get(planName);
            if (planEntry) {
              if (loanSource.sourceType === "L") {
                planEntry.เงินกู้ในประเทศ += amount / 1000000;
              } else if (loanSource.sourceType === "F") {
                planEntry.เงินกู้ต่างประเทศ += amount / 1000000;
              }
            }

            // Update source data for this plan type
            const sourceMap = sourceDataByPlanType.get(planName);
            if (sourceMap) {
              const sourceData = sourceMap.get(sourceName);
              if (sourceData) {
                if (loanSource.sourceType === "L") {
                  sourceData.เงินกู้ในประเทศ += amount / 1000000;
                } else if (loanSource.sourceType === "F") {
                  sourceData.เงินกู้ต่างประเทศ += amount / 1000000;
                }
              }
            }
          });
        }
      });

      // Calculate totals and populate loanSourceData for each plan type
      planTypeMap.forEach((planEntry, planName) => {
        // Calculate total for the plan type
        planEntry.totalDisplay =
          planEntry.เงินกู้ต่างประเทศ + planEntry.เงินกู้ในประเทศ;

        // Create loan source data for this plan type
        const sourceMap = sourceDataByPlanType.get(planName);
        if (sourceMap) {
          sourceMap.forEach((amounts, sourceName) => {
            // Only include sources with non-zero values
            if (amounts.เงินกู้ในประเทศ > 0 || amounts.เงินกู้ต่างประเทศ > 0) {
              planEntry.loanSourceData.push({
                name: sourceName,
                เงินกู้ในประเทศ: amounts.เงินกู้ในประเทศ,
                เงินกู้ต่างประเทศ: amounts.เงินกู้ต่างประเทศ,
                totalDisplaySource:
                  amounts.เงินกู้ในประเทศ + amounts.เงินกู้ต่างประเทศ,
              });
            }
          });
        }
      });

      // Return the data in the required format
      return {
        paymentPlanData: Array.from(planTypeMap.values()),
        totalAmount: totalAmount,
      };
    },

    // Get total loan amount for header display
    get getTotalLoanAmount() {
      if (
        !self.existingDebtPlanPaymentAndRestructureSummary ||
        !self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary
      ) {
        return 0;
      }

      return (
        self.existingDebtPlanPaymentAndRestructureSummary.paymentPlanSummary
          .paymentPlanSumAmountTHB / 1000000 || 0
      );
    },
  }))
  .actions((self: any) => ({
    getNewDebtPlanSummary: flow(function* () {
      try {
        console.log("planid", self.planid);
        const result = yield newDebtPlanAPI.getNewDebtPlanSummary({
          startYear: self.fiscalYear,
          planRelease: self.planRelease,
          planID: self.planid != null ? self.planid : null, // ถ้ามีค่า ส่ง planid, ถ้าไม่มี ส่ง null
          organizationID: self.organization.shareHolderID,
        });
        self.newDebtPlanSummary = result.data;
        console.log("self.newDebtPlanSummary", self.newDebtPlanSummary);
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    getExistingDebtPlanSummary: flow(function* () {
      try {
        const result = yield existingDebtPlanAPI.getExistingDebtPlanSummary({
          startYear: self.fiscalYear,
          planRelease: self.planRelease,
          planID: null, // รหัส แผน ถ้าใส่ null คือรวมทุกแผน ในปีที่ระบุ
          organizationID: self.organization.shareHolderID,
        });
        self.existingDebtPlanSummary = result.data;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    getExistingDebtPlanPaymentAndRestructureSummary: flow(function* () {
      try {
        const result = yield existingDebtPlanAPI.getExistingDebtPlanPaymentAndRestructureSummary({
          startYear: self.fiscalYear,
          planRelease: self.planRelease || "",
          planID: self.planId,
          organizationID: self.organization.shareHolderID || "",
        });
        
        self.existingDebtPlanPaymentAndRestructureSummary = result.data;
      } catch (e) {
        console.error("❌ API Error:", e);
      }
    }),
    getFinancialReportSummary: flow(function* () {
      try {
        const result = yield financialReportAPI.getFinancialReportSummary({
          startYear: self.fiscalYear,
          planRelease: self.planRelease,
          planID: null, // รหัส แผน ถ้าใส่ null คือรวมทุกแผน ในปีที่ระบุ
          organizationID: self.organization.shareHolderID,
        });
        self.financialReport.debtSummary = result.data;
        if (
          self.financialReport.debtSummary.orgLocalDebtEstimation.length === 0
        ) {
          self.financialReport.createOrgLocalDebtEstimation();
        }
        if (
          self.financialReport.debtSummary.govLocalDebtEstimation.length === 0
        ) {
          self.financialReport.createGovLocalDebtEstimation();
        }
        if (
          self.financialReport.debtSummary.orgForeignDebtEstimationList
            .length === 0
        ) {
          self.financialReport.createOrgForeignDebtEstimationList();
        }
        if (
          self.financialReport.debtSummary.govForeignDebtEstimationList
            .length === 0
        ) {
          self.financialReport.createGovForeignDebtEstimationList();
        }
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    onSearch: flow(function* () {
      try {
        self.loading = true;
        yield Promise.all([
          self.newDebtPlanList.getNewDebtPlanList(), // GET List ของ New debt
          self.existingDebtPlanList.getExistingDebtPlanList(), // GET List ของ Existing debt
          self.financialReportList.getFinancialReportList(), // GET List ของ Financial debt
          // self.getNewDebtPlanSummary(), // GET New debt summary สำหรับ Dashboard
          // self.getExistingDebtPlanSummary(), // GET Existing debt summary สำหรับ Dashboard
          // self.getFinancialReportSummary(), // เอาออกไปก่อน load นานเกิน
        ]);
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    setPlanRelease: (planRelease: string) => {
      self.setField({
        fieldname: "planRelease",
        value: planRelease,
      });
      self.newDebtPlanList.setField({
        fieldname: "planRelease",
        value: planRelease,
      });
      self.existingDebtPlanList.setField({
        fieldname: "planRelease",
        value: planRelease,
      });
      self.financialReportList.setField({
        fieldname: "planRelease",
        value: planRelease,
      });
    },
    setShareHolder: (id: number | null, description: string) => {
      self.organization.setShareHolder(id, description);

      self.newDebtPlanList.setField({
        fieldname: "organizationID",
        value: id,
      });

      self.existingDebtPlanList.setField({
        fieldname: "organizationID",
        value: id,
      });

      self.financialReportList.setField({
        fieldname: "organizationID",
        value: id,
      });
    },
    setPlanYear: (year: number) => {
      self.fiscalYear = year;
      self.fiscalYearLabel = `${year}`;
      self.newDebtPlanList.setField({
        fieldname: "year",
        value: year,
      });
      self.existingDebtPlanList.setField({
        fieldname: "year",
        value: year,
      });
      self.financialReportList.setField({
        fieldname: "year",
        value: year,
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setPlanId: (id: number) => {
      self.planId = id;
    },
    setPlanParameters: (params: {
      fiscalYear?: number;
      planRelease?: string;
      planId?: number;
      organizationId?: number;
    }) => {
      if (params.fiscalYear !== undefined) {
        self.fiscalYear = params.fiscalYear;
      }

      if (params.planRelease !== undefined) {
        self.planRelease = params.planRelease;
      }

      if (params.planId !== undefined) {
        self.planId = params.planId;
      }

      if (params.organizationId !== undefined) {
        if (!self.organization) {
          self.organization = {};
        }
        self.organization.shareHolderID = params.organizationId;
      }
    },
    resetAll: () => {
      applySnapshot(self, {});
    },
  }));

export type IYearPlanDashboardModel = typeof YearPlanDashboardModel.Type;
